import { useMemo } from "react"
import { useUser, WorkTaskInfo } from "@tm/context-distribution"
import { BasketMemoContext } from "@tm/models"
import { createBasketMemo, CreateBasketMemoOptions, useWorkTaskTruckData } from "@tm/utils"
import { WorkTask } from "../data/model"
import { getBundleParams } from "../utils"

export type BasketMemo = {
    position: string | undefined
    order: string | undefined
}

export function returnBasketMemo(memo?: string | undefined, basketMemoContext?: BasketMemoContext | undefined): BasketMemo {
    return basketMemoContext === "perOrder"
        ? {
              position: undefined,
              order: memo,
          }
        : {
              position: memo,
              order: undefined,
          }
}

/**
 * @memberof Basket
 * This hook will return the basket memo containing the information of the supplied work task. It will be created based on the current user settings.
 * @returns An object containing the basket memo for a single position (e.g. part) and the basket memo for the whole order depending on the current user settings.
 */
export function useBasketMemo(workTask: WorkTaskInfo | WorkTask | undefined, isNotVehicleDependent?: boolean): BasketMemo {
    // eslint-disable-next-line no-nested-ternary
    const workTaskId = workTask ? ("workTaskId" in workTask ? workTask.workTaskId : workTask.id) : undefined

    const { workTaskTruckData } = useWorkTaskTruckData(workTaskId)
    const {
        basketMemoSections = [],
        basketMemoContext,
        repairShopResponse: {
            addWorkTaskInfoToOrderItemMemo = false,
            workTaskInfoOnlyForVehicleParts = getBundleParams().workTaskInfoOnlyForVehicleParts,
        } = {},
    } = useUser()?.userSettings?.orderOptions ?? {}

    const createMemo = useMemo(
        () => addWorkTaskInfoToOrderItemMemo && (!isNotVehicleDependent || !workTaskInfoOnlyForVehicleParts),
        [addWorkTaskInfoToOrderItemMemo, isNotVehicleDependent, workTaskInfoOnlyForVehicleParts]
    )

    const memo = useMemo<BasketMemo>(() => {
        if (!createMemo) {
            return returnBasketMemo()
        }

        const createBasketMemoOptions: CreateBasketMemoOptions = {
            config: {
                sections: basketMemoSections,
            },
            truckData: workTaskTruckData,
            ...(workTask && "workTaskId" in workTask
                ? {
                      vehicle: workTask.vehicleInfo,
                      customer: workTask.customerInfo,
                      workTaskVoucherNo: workTask.voucherNo,
                  }
                : {
                      vehicle: workTask?.vehicle,
                      customer: workTask?.customer,
                      workTaskVoucherNo: workTask?.workTaskReadModel?.voucherNo,
                  }),
        }

        return returnBasketMemo(createBasketMemo(createBasketMemoOptions), basketMemoContext)
    }, [basketMemoContext, basketMemoSections, workTask, workTaskTruckData, createMemo])

    return memo
}
